import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '../layouts/Header';
import Footer from '../layouts/Footer';
import { Badge, Button, Card, Col, Dropdown, Form, InputGroup, Row, Spinner, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import { leaveSaveAlert } from '../utils/leaveSaveResult';
import axiosInstance from '../services/axiosInterceptor';
import useStudyYearSelector from '../discipline/useStudyYearSelector';
import { leaveSourceLabel } from '../utils/leaveSource';
import { leaveRangeShort } from '../utils/leaveRange';
import ThaiDateInput from './ThaiDateInput'
import { toISODate } from '../utils/thaiDate'

const LEAVE_TYPES = [
  { value: 'sick',     label: 'ลาป่วย', bg: 'warning' },
  { value: 'personal', label: 'ลากิจ',  bg: 'info' },
];
const typeInfo = (t) => LEAVE_TYPES.find(x => x.value === t) || { label: t, bg: 'secondary' };
const todayStr = () => toISODate(new Date());
const pad2 = (n) => String(n).padStart(2, '0');
const addDays = (dateStr, days) => {
  const [y, m, d] = dateStr.split('-').map(Number);
  const dt = new Date(y, m - 1, d + days);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
};
// จำนวนวันลาแบบนับรวมวันเริ่มและวันสิ้นสุด (0 = ช่วงวันที่ไม่ถูกต้อง)
const dayCount = (start, end) => {
  if (!start || !end || start > end) return 0;
  const [y1, m1, d1] = start.split('-').map(Number);
  const [y2, m2, d2] = end.split('-').map(Number);
  return Math.round((new Date(y2, m2 - 1, d2) - new Date(y1, m1 - 1, d1)) / 86400000) + 1;
};
const DAY_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
const overlaps = (aStart, aEnd, bStart, bEnd) => aStart <= bEnd && bStart <= aEnd;

// หน้าแจ้งลานักเรียนของฝ่ายที่แจ้งให้นักเรียนคนไหนก็ได้ (ไม่จำกัดห้องที่ปรึกษา) —
// ฝ่ายปกครองกับหัวหน้าวิชาการสามัญใช้หน้าเดียวกัน ต่างกันแค่ endpoint และสิทธิ์ลบ
// ส่วน source ที่บันทึกลง tb_student_leave ฝั่ง server เป็นคนกำหนดจาก route
export default function StudentLeaveBoard({
  roleLabel,                       // ชื่อฝ่ายใน breadcrumb
  title,
  intro,                           // ข้อความในกล่องแนะนำ
  leavesPath,                      // endpoint รายการลา (GET/POST/DELETE)
  searchPath,                      // endpoint ค้นหานักเรียน
  canDeleteLeave = () => true,     // ลบรายการนี้ได้ไหม
}) {
  const setSkin = useState(localStorage.getItem('skin-mode') ? 'dark' : '')[1];
  const { yearList, yearId, yearLabel, handleSelectYear } = useStudyYearSelector();

  const [keyword,   setKeyword]   = useState('');
  const [students,  setStudents]  = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched,  setSearched]  = useState(false);

  const [leaves, setLeaves] = useState([]);
  // การลาที่นักเรียนในผลค้นหามีอยู่แล้ว — คนละชุดกับ leaves ด้านบนซึ่งเป็นรายการรวม
  // ทั้งปีและติด LIMIT ฝั่ง server คนที่ค้นเจอจึงอาจไม่อยู่ในนั้น
  const [searchedLeaves, setSearchedLeaves] = useState([]);

  // ฟอร์มแจ้งลา
  const [selectedIds, setSelectedIds] = useState([]);   // study_id ที่เลือก
  const [leaveStart,  setLeaveStart]  = useState(todayStr());
  const [leaveEnd,    setLeaveEnd]    = useState(todayStr());
  const [leaveType,   setLeaveType]   = useState('sick');
  const [leaveNote,   setLeaveNote]   = useState('');
  const [saving,      setSaving]      = useState(false);

  const [message, setMessage] = useState('');
  const [error,   setError]   = useState('');

  useEffect(() => {
    if (!message) return;
    const t = setTimeout(() => setMessage(''), 4000);
    return () => clearTimeout(t);
  }, [message]);

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(''), 6000);
    return () => clearTimeout(t);
  }, [error]);

  // ─── โหลดรายการลาทั้งหมดของปีการศึกษา ────────────────────────────────────────
  const loadLeaves = useCallback(() => {
    if (!yearId) return;
    axiosInstance.get(`${leavesPath}?yearId=${yearId}`)
      .then(res => setLeaves(res.data || []))
      .catch(() => setLeaves([]));
  }, [yearId, leavesPath]);

  useEffect(() => {
    setStudents([]);
    setSearchedLeaves([]);
    setSelectedIds([]);
    setSearched(false);
    loadLeaves();
  }, [yearId, loadLeaves]);

  // ─── การลาที่นักเรียนกลุ่มที่ค้นเจอมีอยู่แล้ว (ทุกฝ่ายที่แจ้งได้) ─────────────────
  const loadSearchedLeaves = useCallback((list) => {
    const ids = (list || []).map(s => s.study_id).filter(Boolean);
    if (!yearId || ids.length === 0) { setSearchedLeaves([]); return; }
    axiosInstance.get(`${leavesPath}?yearId=${yearId}&studyIds=${ids.join(',')}`)
      .then(res => setSearchedLeaves(res.data || []))
      .catch(() => setSearchedLeaves([]));
  }, [yearId, leavesPath]);

  // ─── ค้นหานักเรียน (เลขประจำตัว หรือ ชื่อ) ────────────────────────────────────
  const search = useCallback((e) => {
    e?.preventDefault();
    if (!yearId) return;
    setSearching(true);
    setError('');
    axiosInstance
      .get(`${searchPath}?yearId=${yearId}&keyword=${encodeURIComponent(keyword.trim())}`)
      .then(res => {
        const list = res.data || [];
        setStudents(list);
        loadSearchedLeaves(list);
      })
      .catch(() => setError('ไม่สามารถค้นหานักเรียนได้'))
      .finally(() => { setSearching(false); setSearched(true); });
  }, [yearId, keyword, searchPath, loadSearchedLeaves]);

  // คนที่มีการแจ้งลาคาบเกี่ยวกับช่วงที่กำลังกรอกอยู่แล้ว — ไม่ว่าฝ่ายไหนเป็นคนแจ้ง
  // (server จะข้ามคนกลุ่มนี้ตอนบันทึก ดู utils/leaveOverlap.js)
  const leavesByStudy = useMemo(() => {
    const map = new Map();
    searchedLeaves.forEach(l => {
      const key = String(l.study_id);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(l);
    });
    return map;
  }, [searchedLeaves]);

  const existingFor = useCallback((studyId) => (
    (leavesByStudy.get(String(studyId)) || [])
      .filter(l => overlaps(leaveStart, leaveEnd, l.leave_start, l.leave_end))
  ), [leavesByStudy, leaveStart, leaveEnd]);

  // ─── ฟอร์ม ─────────────────────────────────────────────────────────────────
  const toggleStudent = (studyId) => {
    setSelectedIds(prev => prev.includes(studyId)
      ? prev.filter(id => id !== studyId)
      : [...prev, studyId]);
  };
  // "เลือกทั้งหมด" = ทุกคนที่ยังไม่มีใครแจ้งลาช่วงนี้ — เลือกคนที่ลาอยู่แล้วมาด้วย
  // ก็ถูก server ข้ามอยู่ดี แต่ผู้ใช้จะนึกว่าบันทึกครบ
  const toggleAll = () => {
    const ids = students.filter(s => existingFor(s.study_id).length === 0).map(s => s.study_id);
    setSelectedIds(prev => ids.length > 0 && ids.every(id => prev.includes(id)) ? [] : ids);
  };

  const handleStartChange = (val) => {
    const days = dayCount(leaveStart, leaveEnd);
    setLeaveStart(val);
    setLeaveEnd(days > 0 ? addDays(val, days - 1) : val);   // คงจำนวนวันลาเดิมไว้
  };

  // เลือกจำนวนวัน → คำนวณวันที่สิ้นสุดให้จากวันที่เริ่ม
  const handleDaysChange = (val) => {
    if (!val) return;                                       // "กำหนดเอง" = ไม่แตะวันที่
    setLeaveEnd(addDays(leaveStart, Number(val) - 1));
  };

  const resetForm = () => {
    setSelectedIds([]);
    setLeaveStart(todayStr());
    setLeaveEnd(todayStr());
    setLeaveType('sick');
    setLeaveNote('');
  };

  const handleSave = () => {
    if (selectedIds.length === 0) {
      Swal.fire({ icon: 'warning', title: 'ยังไม่ได้เลือกนักเรียน', text: 'กรุณาเลือกนักเรียนอย่างน้อย 1 คน' });
      return;
    }
    if (leaveStart > leaveEnd) {
      Swal.fire({ icon: 'warning', title: 'ช่วงวันที่ไม่ถูกต้อง', text: 'วันที่เริ่มต้องไม่เกินวันที่สิ้นสุด' });
      return;
    }
    setSaving(true);
    axiosInstance.post(leavesPath, {
      studyIds:  selectedIds,
      yearId,
      leaveStart,
      leaveEnd,
      leaveType,
      leaveNote: leaveNote.trim() || null,
    })
      .then((res) => {
        Swal.fire(leaveSaveAlert(res.data));
        resetForm();
        loadLeaves();
        loadSearchedLeaves(students);
      })
      .catch(err => {
        Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถบันทึกได้' });
      })
      .finally(() => setSaving(false));
  };

  const handleDelete = (leave) => {
    Swal.fire({
      title: 'ยืนยันการลบ',
      text: `ลบการลาของ ${leave.st_name}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ลบ',
      cancelButtonText: 'ยกเลิก',
      confirmButtonColor: '#dc3545',
    }).then(result => {
      if (!result.isConfirmed) return;
      axiosInstance.delete(`${leavesPath}/${leave.leave_id}`)
        .then(() => {
          Swal.fire({ icon: 'success', title: 'ลบสำเร็จ', timer: 1000, showConfirmButton: false });
          loadLeaves();
          loadSearchedLeaves(students);
        })
        .catch(err => {
          Swal.fire({ icon: 'error', title: 'เกิดข้อผิดพลาด', text: err.response?.data?.message || 'ไม่สามารถลบได้' });
        });
    });
  };

  const today = todayStr();
  const leaveDays = dayCount(leaveStart, leaveEnd);
  const coversToday = (l) => l.leave_start <= today && today <= l.leave_end;
  const selectableCount = students.filter(s => existingFor(s.study_id).length === 0).length;
  const allSelected = selectableCount > 0 && selectedIds.length === selectableCount;
  const overlapSelected = selectedIds.filter(id => existingFor(id).length > 0).length;

  return (
    <>
      <Header onSkin={setSkin} />
      <div className="main main-app p-3 p-lg-4">

        {/* page header */}
        <div className="d-flex flex-wrap align-items-start justify-content-between gap-2 mb-4">
          <div>
            <ol className="breadcrumb fs-sm mb-1">
              <li className="breadcrumb-item">{roleLabel}</li>
              <li className="breadcrumb-item active">แจ้งลานักเรียน</li>
            </ol>
            <h4 className="main-title mb-0">{title}</h4>
          </div>
          <Dropdown>
            <Dropdown.Toggle variant="outline-primary">
              {yearLabel || 'เลือกปีการศึกษา'}
            </Dropdown.Toggle>
            <Dropdown.Menu>
              {yearList.map(y => (
                <Dropdown.Item key={y.year_id} onClick={() => handleSelectYear(y.year_id)}>
                  ปีการศึกษา {y.year_year}
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="alert alert-info d-flex align-items-center gap-2 mb-4" role="alert">
          <i className="ri-information-line fs-5"></i>
          <span>{intro}</span>
        </div>

        {/* alerts */}
        {message && (
          <div className="alert alert-success alert-dismissible d-flex align-items-center gap-2 mb-3" role="alert">
            ✓ {message}
            <button type="button" className="btn-close ms-auto" onClick={() => setMessage('')} />
          </div>
        )}
        {error && (
          <div className="alert alert-danger alert-dismissible d-flex align-items-center gap-2 mb-3" role="alert">
            ⚠ {error}
            <button type="button" className="btn-close ms-auto" onClick={() => setError('')} />
          </div>
        )}

        <Row className="g-3">
          {/* ── ฟอร์มแจ้งลา ── */}
          <Col xl="5">
            <Card className="card-one h-100">
              <Card.Header><Card.Title as="h6">เพิ่มการลา</Card.Title></Card.Header>
              <Card.Body className="p-3">
                <Row className="g-2 mb-3">
                  <Col xs="12">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">จำนวนวันลา</Form.Label>
                      <Form.Select
                        value={DAY_OPTIONS.includes(leaveDays) ? String(leaveDays) : ''}
                        onChange={e => handleDaysChange(e.target.value)}
                      >
                        <option value="">กำหนดเอง</option>
                        {DAY_OPTIONS.map(d => <option key={d} value={d}>{d} วัน</option>)}
                      </Form.Select>
                      <Form.Text className="text-muted">เลือกจำนวนวันแล้วระบบจะตั้งวันที่สิ้นสุดให้อัตโนมัติ</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col xs="6">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">วันที่เริ่ม</Form.Label>
                      <ThaiDateInput value={leaveStart} onChange={v => handleStartChange(v)} />
                    </Form.Group>
                  </Col>
                  <Col xs="6">
                    <Form.Group>
                      <Form.Label className="fw-semibold mb-1">วันที่สิ้นสุด</Form.Label>
                      <ThaiDateInput value={leaveEnd} min={leaveStart} onChange={v => setLeaveEnd(v)} />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold mb-1">ประเภทการลา</Form.Label>
                  <div className="d-flex gap-3">
                    {LEAVE_TYPES.map(t => (
                      <Form.Check
                        key={t.value}
                        type="radio"
                        name="leaveType"
                        id={`leaveType-${t.value}`}
                        label={t.label}
                        checked={leaveType === t.value}
                        onChange={() => setLeaveType(t.value)}
                      />
                    ))}
                  </div>
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label className="fw-semibold mb-1">หมายเหตุ</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={2}
                    placeholder="เหตุผลการลา (ถ้ามี)"
                    value={leaveNote}
                    onChange={e => setLeaveNote(e.target.value)}
                    maxLength={255}
                  />
                </Form.Group>

                <Form.Group>
                  <Form.Label className="fw-semibold mb-1">ค้นหานักเรียน</Form.Label>
                  <Form onSubmit={search}>
                    <InputGroup>
                      <Form.Control
                        value={keyword}
                        onChange={e => setKeyword(e.target.value)}
                        placeholder="เลขประจำตัว หรือชื่อนักเรียน"
                        style={{ fontSize: 16 }}
                      />
                      <Button type="submit" variant="primary" disabled={searching}>
                        {searching
                          ? <Spinner animation="border" size="sm" />
                          : <><i className="ri-search-line me-1"></i>ค้นหา</>}
                      </Button>
                    </InputGroup>
                  </Form>
                </Form.Group>

                <Form.Group className="mt-3">
                  <div className="d-flex align-items-center justify-content-between mb-1">
                    <Form.Label className="fw-semibold mb-0">เลือกนักเรียน ({selectedIds.length})</Form.Label>
                    {students.length > 0 && (
                      <Button variant="link" size="sm" className="p-0" onClick={toggleAll}>
                        {allSelected ? 'ยกเลิกทั้งหมด' : 'เลือกทั้งหมด'}
                      </Button>
                    )}
                  </div>
                  <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #dee2e6', borderRadius: 6 }} className="p-2">
                    {students.length > 0 ? students.map(s => {
                      const already = existingFor(s.study_id);
                      return (
                        <div key={s.study_id} className="d-flex align-items-center gap-2 flex-wrap">
                          <Form.Check
                            type="checkbox"
                            id={`st-${s.study_id}`}
                            className="flex-grow-1"
                            label={`${s.st_idnumber} — ${s.st_name} (ม.${s.c_id}/${s.r_no})`}
                            checked={selectedIds.includes(s.study_id)}
                            onChange={() => toggleStudent(s.study_id)}
                          />
                          {/* บอกว่าฝ่ายไหนแจ้งไว้แล้วและช่วงวันไหน — ถ้าไม่บอกช่วงวัน
                              ผู้ใช้จะแยกไม่ออกว่าที่กำลังจะแจ้งเป็นการแจ้งซ้ำหรือคนละช่วง */}
                          {already.map(l => (
                            <Badge key={l.leave_id} bg="info" className="fw-normal"
                              title={`${l.leave_start} ถึง ${l.leave_end}`}>
                              {leaveSourceLabel(l.source)}ลาแล้ว {leaveRangeShort(l.leave_start, l.leave_end)}
                            </Badge>
                          ))}
                        </div>
                      );
                    }) : (
                      <div className="text-muted text-center py-2">
                        {searched ? 'ไม่พบนักเรียน' : 'ค้นหาเพื่อเลือกนักเรียน'}
                      </div>
                    )}
                  </div>
                  {overlapSelected > 0 && (
                    <div className="alert alert-warning mt-2 mb-0 py-2 px-3 fs-sm">
                      <i className="ri-alert-line me-1"></i>
                      เลือกไว้ {overlapSelected} คน ที่มีการแจ้งลาช่วงวันนี้อยู่แล้ว — ระบบจะข้ามให้ ไม่บันทึกซ้ำ
                    </div>
                  )}
                </Form.Group>

                <div className="mt-3 d-flex justify-content-end">
                  <Button variant="success" onClick={handleSave} disabled={saving}>
                    {saving
                      ? <><Spinner animation="border" size="sm" className="me-1" />กำลังบันทึก...</>
                      : <><i className="ri-save-line me-1"></i>บันทึกการลา</>}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>

          {/* ── ตารางรายการลา ── */}
          <Col xl="7">
            <Card className="card-one h-100">
              <Card.Header className="d-flex align-items-center justify-content-between">
                <Card.Title as="h6" className="mb-0">รายการลาล่าสุด</Card.Title>
                {leaves.length > 0 && <Badge bg="secondary" className="fw-normal">{leaves.length} รายการ</Badge>}
              </Card.Header>
              <Card.Body className="p-3">
                <Table responsive hover className="mb-0">
                  <thead>
                    <tr>
                      <th scope="col">ชื่อ-สกุล</th>
                      <th scope="col" className="text-center" style={{ width: 70 }}>ห้อง</th>
                      <th scope="col" className="text-center" style={{ width: 170 }}>ช่วงวันลา</th>
                      <th scope="col" className="text-center" style={{ width: 80 }}>ประเภท</th>
                      <th scope="col">หมายเหตุ</th>
                      <th scope="col" className="text-center" style={{ width: 50 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaves.length > 0 ? leaves.map(l => (
                      <tr key={l.leave_id} className={coversToday(l) ? 'table-info' : ''}>
                        <td>
                          {l.st_name}
                          <div className="fs-xs text-secondary">
                            {l.st_idnumber}
                            {' · '}
                            <span className={l.source && l.source !== 'advisor' ? 'text-primary' : ''}>
                              {leaveSourceLabel(l.source)}
                            </span>
                          </div>
                        </td>
                        <td className="text-center">{l.tb_class_c_id}/{l.r_no}</td>
                        <td className="text-center">
                          {l.leave_start === l.leave_end
                            ? l.leave_start
                            : <>{l.leave_start}<br />ถึง {l.leave_end}</>}
                        </td>
                        <td className="text-center">
                          <Badge bg={typeInfo(l.leave_type).bg}>{typeInfo(l.leave_type).label}</Badge>
                        </td>
                        <td><small>{l.leave_note || '-'}</small></td>
                        <td className="text-center">
                          {canDeleteLeave(l) && (
                            <Button variant="outline-danger" size="sm" onClick={() => handleDelete(l)} title="ลบ">
                              <i className="ri-delete-bin-line"></i>
                            </Button>
                          )}
                        </td>
                      </tr>
                    )) : (
                      <tr><td colSpan={6} className="text-center text-muted py-3">ยังไม่มีรายการลา</td></tr>
                    )}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Footer />
      </div>
    </>
  );
}
